"use client";

import { c as _c } from "react-compiler-runtime";
import { useSearchParams } from "next/navigation";
import { type AgelessAssessmentFlowType, AgelessAssessmentUsageContext } from "@orval/model";
import { useCurrentAssessment } from "../AssessmentSessionProvider";
import { type AssessmentSessionStorageScope, assessmentSessionStorageService } from "../AssessmentSessionStorageService";
import { type AssessmentUsageContextCapabilities, CONTEXT_LOCATION_PARAM, LEGACY_AT_LOCATION_PARAM, parseUsageContextFromSearchParams } from "./assessmentUsageContext.types";
import { getCapabilitiesForUsageContext } from "./assessmentUsageContextCapabilities";
type UseAssessmentUsageContextArgs = {
  subdomainSlug: string;
  flowType: AgelessAssessmentFlowType;
};
export type AssessmentUsageContext = {
  readonly usageContext: AgelessAssessmentUsageContext;
  readonly capabilities: AssessmentUsageContextCapabilities;
  readonly locationId: string | null;
};

/**
 * Reader for the resolved usage context. After define, prefers the assessment
 * context from the server; before that, URL and session entry state.
 * Persistence of entry params is owned by `AssessmentGate`; this hook never writes.
 */
export function useAssessmentUsageContext(t0) {
  const $ = _c(11);
  const {
    subdomainSlug,
    flowType
  } = t0;
  const searchParams = useSearchParams();
  const {
    assessment
  } = useCurrentAssessment();
  let locationId;
  let t1;
  let t2;
  if ($[0] !== assessment?.context || $[1] !== flowType || $[2] !== searchParams || $[3] !== subdomainSlug) {
    const {
      usageContext,
      locationId: t3
    } = resolveAssessmentUsageContext(searchParams, {
      subdomainSlug,
      flowType
    }, {
      assessmentContext: assessment?.context
    });
    locationId = t3;
    t1 = usageContext;
    t2 = getCapabilitiesForUsageContext(usageContext);
    $[0] = assessment?.context;
    $[1] = flowType;
    $[2] = searchParams;
    $[3] = subdomainSlug;
    $[4] = locationId;
    $[5] = t1;
    $[6] = t2;
  } else {
    locationId = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  let t3;
  if ($[7] !== locationId || $[8] !== t1 || $[9] !== t2) {
    t3 = {
      usageContext: t1,
      capabilities: t2,
      locationId
    };
    $[7] = locationId;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
export type ResolveAssessmentUsageContextOptions = {
  /**
   * Loaded assessment context from the server. When non-`Standard`, wins over
   * session storage so in-flow UI tracks the context set at define.
   * Callers without a loaded assessment (e.g. define payload) omit this.
   */
  readonly assessmentContext?: AgelessAssessmentUsageContext | null;
};

/**
 * Resolve usage context and location: URL params, then a non-`Standard`
 * assessment context, then session storage, then legacy `at_location`. Pure and
 * hook-free so it can run inside async callbacks (e.g. right after define).
 */
export function resolveAssessmentUsageContext(searchParams: URLSearchParams, scope: AssessmentSessionStorageScope, options?: ResolveAssessmentUsageContextOptions): {
  usageContext: AgelessAssessmentUsageContext;
  locationId: string | null;
} {
  const paramUsageContext = parseUsageContextFromSearchParams(searchParams);
  const paramLocationId = searchParams.get(CONTEXT_LOCATION_PARAM);
  const legacyAtLocationParam = searchParams.get(LEGACY_AT_LOCATION_PARAM) === "true";
  return {
    usageContext: resolveUsageContext(paramUsageContext, legacyAtLocationParam, scope, options?.assessmentContext),
    locationId: paramLocationId ?? assessmentSessionStorageService.getContextLocationId(scope)
  };
}
function resolveUsageContext(paramUsageContext: AgelessAssessmentUsageContext | null, legacyAtLocationParam: boolean, scope: AssessmentSessionStorageScope, assessmentContext?: AgelessAssessmentUsageContext | null): AgelessAssessmentUsageContext {
  if (paramUsageContext) {
    return paramUsageContext;
  }
  if (assessmentContext && assessmentContext !== AgelessAssessmentUsageContext.Standard) {
    return assessmentContext;
  }
  const persistedContextMode = assessmentSessionStorageService.getContextMode(scope);
  if (persistedContextMode === AgelessAssessmentUsageContext.InClinic) {
    return AgelessAssessmentUsageContext.InClinic;
  }
  if (persistedContextMode === AgelessAssessmentUsageContext.WebsiteEmbed) {
    return AgelessAssessmentUsageContext.WebsiteEmbed;
  }

  // Back-compat: the legacy `at_location=true` link (URL param or persisted flag)
  // maps to the In Clinic context.
  if (legacyAtLocationParam || assessmentSessionStorageService.isAtLocationModeEnabled(scope)) {
    return AgelessAssessmentUsageContext.InClinic;
  }
  return AgelessAssessmentUsageContext.Standard;
}